/**
 * Gestion › Enfants › Consulter.
 *
 * Refait le 26/10/2026 sur le modèle des écrans de présences (voir
 * `consultation/regles.ts`) : une recherche qui trouve un enfant par son nom,
 * celui de son parent ou son adresse électronique ; une semaine d'un centre,
 * proposée comme dans Présences, avec « Enfants | À savoir » ; la fiche en
 * pleine page, relue à chaque ouverture dans le modèle du parent, et
 * « Modifier » qui mène à l'écran de modification.
 */

import { useEffect, useMemo, useState } from 'react'
import { AlertCircle, Copy, Loader2, RefreshCw, Search, X } from 'lucide-react'
import { anneeScolaireCourante } from '../../../lib/classes/regles'
import type { ClasseDuCentre } from '../../../lib/classes/regles'
import { ageEnClair, semaineEnClair } from '../../../lib/presences/lien'
import { semaineProposee, semainesDuCentre } from '../../../lib/presences/session'
import type { Fiche } from '../../../components/forms/kid/fiche'
import { DuplicateKidsPanel } from './DuplicateKidsPanel'
import { KidsEditForm } from './KidsEditForm'
import { FicheConsultee } from './consultation/Fiche'
import { Avatar, LigneASavoirVue, LigneEnfant } from './consultation/Lignes'
import {
  aSavoir,
  aSignaler,
  grouperLaSemaine,
  lireRecherche,
  signes,
  type EnfantDeLaSemaine,
  type LigneASavoir,
  type PersonneConnue,
} from './consultation/regles'
import {
  lireLaSemaine,
  lireLesClasses,
  lireLesEcoles,
  lireLesFiches,
  lireLesSessions,
  rechercherEnfants,
  signerLesPhotos,
  type CentreConsulte,
  type EnfantTrouve,
  type FichesLues,
  type SessionConsultee,
} from './consultation/lecture'

interface KidsManagementProps {
  initialParams?: {
    centerId?: string
  } | null
  onParamsConsumed?: () => void
  /** L'administration : « Modifier » depuis la fiche. */
  peutModifier?: boolean
}

const aujourdHui = () => new Date().toISOString().slice(0, 10)

/** Ce qu'on sait des enfants affichés : leurs fiches, photos et classes. */
interface Details {
  fiches: FichesLues
  photos: Map<string, string>
  classes: Map<string, ClasseDuCentre>
}

const VIDE: Details = {
  fiches: { fiches: new Map(), kids: new Map(), personnes: [] },
  photos: new Map(),
  classes: new Map(),
}

async function lireLesDetails(kidIds: string[], annee: number): Promise<Details> {
  const [fiches, classes] = await Promise.all([
    lireLesFiches(kidIds),
    lireLesClasses(kidIds, annee).catch(() => new Map<string, ClasseDuCentre>()),
  ])
  const photos = await signerLesPhotos([...fiches.kids.values()].map(k => k.photo_url)).catch(() => new Map<string, string>())
  return { fiches, photos, classes }
}

export function KidsManagement({ initialParams, onParamsConsumed, peutModifier = true }: KidsManagementProps = {}) {
  const annee = useMemo(() => anneeScolaireCourante(), [])
  const [ecoles, setEcoles] = useState<Map<string, string>>(new Map())

  /* La recherche */
  const [texte, setTexte] = useState('')
  const recherche = useMemo(() => lireRecherche(texte), [texte])
  const [trouves, setTrouves] = useState<EnfantTrouve[] | null>(null)
  const [detailsTrouves, setDetailsTrouves] = useState<Details>(VIDE)
  const [rechercheEnCours, setRechercheEnCours] = useState(false)
  const [erreurRecherche, setErreurRecherche] = useState<string | null>(null)

  /* La semaine */
  const [centres, setCentres] = useState<CentreConsulte[]>([])
  const [sessions, setSessions] = useState<SessionConsultee[] | null>(null)
  const [centreId, setCentreId] = useState('')
  const [lundi, setLundi] = useState<string | null>(null)
  const [vue, setVue] = useState<'enfants' | 'a_savoir'>('enfants')
  const [enfants, setEnfants] = useState<EnfantDeLaSemaine[] | null>(null)
  const [details, setDetails] = useState<Details>(VIDE)
  const [chargement, setChargement] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)
  const [version, setVersion] = useState(0)

  /* Où l'on est */
  const [ouvert, setOuvert] = useState<string | null>(null)
  const [enModification, setEnModification] = useState<string | null>(null)
  const [doublons, setDoublons] = useState(false)

  useEffect(() => {
    lireLesEcoles().then(setEcoles).catch(() => undefined)
    lireLesSessions()
      .then(({ centres: c, sessions: s }) => {
        setCentres(c)
        setSessions(s)
      })
      .catch(e => setErreur(`Les semaines n’ont pas pu être lues : ${e instanceof Error ? e.message : String(e)}`))
  }, [])

  /* Depuis le tableau de bord : le centre de la tuile. */
  useEffect(() => {
    if (!initialParams?.centerId || centres.length === 0) return
    setCentreId(initialParams.centerId)
    onParamsConsumed?.()
  }, [initialParams, centres, onParamsConsumed])

  const semaines = useMemo(
    () => (sessions ? semainesDuCentre(centreId ? sessions.filter(s => s.centreId === centreId) : sessions) : []),
    [sessions, centreId],
  )
  const semaine = semaines.find(s => s.lundi === lundi) ?? null

  /* La semaine proposée, comme dans Présences : en cours, sinon la prochaine. */
  useEffect(() => {
    if (semaines.length === 0) {
      setLundi(null)
      return
    }
    setLundi(l => (l && semaines.some(s => s.lundi === l) ? l : semaineProposee(semaines, aujourdHui())?.lundi ?? null))
  }, [semaines])

  useEffect(() => {
    if (!semaine) {
      setEnfants(sessions ? [] : null)
      return
    }
    let annule = false
    setChargement(true)
    setErreur(null)
    void (async () => {
      try {
        const lus = await lireLaSemaine(semaine.sessions as SessionConsultee[])
        const d = await lireLesDetails([...new Set(lus.map(e => e.kidId))], annee)
        if (annule) return
        setEnfants(lus)
        setDetails(d)
      } catch (e) {
        if (!annule) setErreur(`La semaine n’a pas pu être lue : ${e instanceof Error ? e.message : String(e)}`)
      } finally {
        if (!annule) setChargement(false)
      }
    })()
    return () => {
      annule = true
    }
    // `semaine` change d'objet à chaque calcul : on suit sa clé.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lundi, centreId, sessions, version, annee])

  useEffect(() => {
    if (recherche.type === 'aucune') {
      setTrouves(null)
      setErreurRecherche(null)
      return
    }
    let annule = false
    const minuterie = setTimeout(() => {
      setRechercheEnCours(true)
      setErreurRecherche(null)
      void (async () => {
        try {
          const r = await rechercherEnfants(recherche)
          if (annule) return
          setTrouves(r)
          const d = await lireLesDetails(r.map(e => e.kidId), annee)
          if (!annule) setDetailsTrouves(d)
        } catch (e) {
          if (!annule) setErreurRecherche(e instanceof Error ? e.message : String(e))
        } finally {
          if (!annule) setRechercheEnCours(false)
        }
      })()
    }, 250)
    return () => {
      annule = true
      clearTimeout(minuterie)
    }
  }, [recherche, annee, version])

  /* --- Ce qui s'affiche ------------------------------------------------- */

  const lignesDe = (d: Details, kidId: string): LigneASavoir[] | null => {
    const f: Fiche | undefined = d.fiches.fiches.get(kidId)
    return f ? aSavoir(f, d.fiches.personnes as PersonneConnue[]) : null
  }
  const photoDe = (d: Details, kidId: string) => {
    const chemin = d.fiches.kids.get(kidId)?.photo_url
    return chemin ? d.photos.get(chemin) ?? null : null
  }
  const ecoleDe = (d: Details, kidId: string, brute?: string | null) => {
    const id = brute ?? d.fiches.kids.get(kidId)?.ecole ?? ''
    return ecoles.get(id) ?? (id || null)
  }
  const sousTitre = (d: Details, kidId: string, dateNaissance: string | null, ecoleBrute?: string | null) =>
    [ageEnClair(dateNaissance, aujourdHui()), ecoleDe(d, kidId, ecoleBrute), d.classes.get(kidId)?.libelle ?? null]
      .filter(Boolean)
      .join(' · ') || null

  const groupes = useMemo(() => grouperLaSemaine(enfants ?? [], !centreId), [enfants, centreId])
  const aSignalerParGroupe = useMemo(
    () => groupes.map(g => ({
      groupe: g,
      enfants: g.enfants.flatMap(e => {
        const lignes = lignesDe(details, e.kidId)
        return lignes && aSignaler(lignes) ? [{ enfant: e, lignes }] : []
      }),
    })),
    [groupes, details],
  )
  const nbASavoir = aSignalerParGroupe.reduce((n, g) => n + g.enfants.length, 0)
  const nbEnfants = new Set((enfants ?? []).map(e => e.kidId)).size

  const annees = useMemo(() => {
    const parAn = new Map<string, typeof semaines>()
    for (const s of semaines) parAn.set(s.lundi.slice(0, 4), [...(parAn.get(s.lundi.slice(0, 4)) ?? []), s])
    return [...parAn.entries()].sort((a, b) => b[0].localeCompare(a[0]))
  }, [semaines])

  if (enModification) {
    return (
      <KidsEditForm
        kidId={enModification}
        onBack={() => {
          setEnModification(null)
          setVersion(v => v + 1)
        }}
      />
    )
  }

  return (
    <>
      {ouvert && (
        <FicheConsultee
          kidId={ouvert}
          ecoles={ecoles}
          onRetour={() => setOuvert(null)}
          onModifier={peutModifier ? () => setEnModification(ouvert) : undefined}
        />
      )}

      {/* La liste reste montée sous la fiche : en revenant, on retrouve sa recherche et sa place. */}
      <div className={ouvert ? 'hidden' : 'space-y-4'}>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Enfants</h1>
            <p className="text-sm text-gray-600">Chercher un enfant, ou voir ceux d’une semaine.</p>
          </div>
          <button
            onClick={() => setDoublons(true)}
            className="flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            <Copy className="h-4 w-4" /> Doublons
          </button>
        </div>

        <label className="relative block">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={texte}
            onChange={e => setTexte(e.target.value)}
            placeholder="Enfant, parent ou e-mail…"
            className="w-full rounded-xl border border-gray-300 bg-white py-3 pl-10 pr-10 text-base shadow-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-500"
            aria-label="Chercher un enfant"
            autoFocus
          />
          {texte && (
            <button
              type="button"
              onClick={() => setTexte('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              aria-label="Effacer la recherche"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </label>

        {recherche.type !== 'aucune' ? (
          <section>
            <p className="mb-2 flex items-center gap-2 text-sm text-gray-600">
              {rechercheEnCours && <Loader2 className="h-4 w-4 animate-spin" />}
              {trouves && !rechercheEnCours && (
                trouves.length === 0
                  ? `Personne ne répond à « ${texte.trim()} ».`
                  : `${trouves.length === 50 ? '50 premiers résultats' : `${trouves.length} enfant${trouves.length > 1 ? 's' : ''}`} — un clic ouvre la fiche.`
              )}
            </p>
            {erreurRecherche && <Erreur texte={`La recherche a échoué : ${erreurRecherche}`} />}
            {trouves && trouves.length > 0 && (
              <ul className="divide-y divide-gray-100 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200">
                {trouves.map(e => {
                  const lignes = lignesDe(detailsTrouves, e.kidId)
                  const parent = `${e.parent?.prenom ?? ''} ${e.parent?.nom ?? ''}`.trim()
                  return (
                    <LigneEnfant
                      key={e.kidId}
                      photo={photoDe(detailsTrouves, e.kidId)}
                      prenom={e.prenom}
                      nom={e.nom}
                      signes={lignes ? signes(lignes) : null}
                      sousTitre={[sousTitre(detailsTrouves, e.kidId, e.dateNaissance, e.ecole), parent ? `parent : ${parent}` : null].filter(Boolean).join(' · ')}
                      onOuvrir={() => setOuvert(e.kidId)}
                    />
                  )
                })}
              </ul>
            )}
          </section>
        ) : (
          <section className="space-y-3">
            <div className="flex flex-wrap items-end gap-2">
              <label className="min-w-[12rem] flex-1">
                <span className="mb-1 block text-xs font-medium text-gray-600">Centre</span>
                <select
                  value={centreId}
                  onChange={e => setCentreId(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-500"
                >
                  <option value="">Tous les centres</option>
                  {centres.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
                </select>
              </label>
              <label className="min-w-[14rem] flex-[2]">
                <span className="mb-1 block text-xs font-medium text-gray-600">Semaine</span>
                <select
                  value={lundi ?? ''}
                  onChange={e => setLundi(e.target.value || null)}
                  disabled={semaines.length === 0}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:ring-2 focus:ring-amber-500 disabled:opacity-50"
                >
                  {semaines.length === 0 && <option value="">Aucune semaine</option>}
                  {annees.map(([an, ss]) => (
                    <optgroup key={an} label={an}>
                      {ss.map(s => (
                        <option key={s.lundi} value={s.lundi}>
                          {s.libelle} · {semaineEnClair(s.lundi, s.vendredi)}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </label>
              <button
                type="button"
                onClick={() => setVersion(v => v + 1)}
                disabled={chargement || !semaine}
                className="flex h-10 w-10 items-center justify-center rounded-lg border border-gray-300 bg-white text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                aria-label="Recharger la semaine"
                title="Recharger la semaine"
              >
                <RefreshCw className={`h-4 w-4 ${chargement ? 'animate-spin' : ''}`} />
              </button>
            </div>

            {erreur && <Erreur texte={erreur} />}

            {semaine && (
              <div className="flex gap-1 rounded-xl bg-gray-100 p-1" role="tablist" aria-label="Vue">
                {(['enfants', 'a_savoir'] as const).map(v => (
                  <button
                    key={v}
                    type="button"
                    role="tab"
                    aria-selected={vue === v}
                    onClick={() => setVue(v)}
                    className={`flex-1 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      vue === v ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {v === 'enfants' ? `Enfants${enfants ? ` (${nbEnfants})` : ''}` : 'À savoir'}
                    {v === 'a_savoir' && enfants && nbASavoir > 0 && (
                      <span className="ml-1.5 inline-flex min-w-[1.4rem] items-center justify-center rounded-full bg-amber-100 px-1.5 text-xs font-semibold text-amber-900">
                        {nbASavoir}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}

            {chargement && (
              <div className="flex justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            )}

            {!chargement && sessions && semaines.length === 0 && (
              <p className="rounded-xl bg-white p-6 text-center text-sm text-gray-600 ring-1 ring-gray-200">
                Aucune semaine de stage pour ce centre. Cherchez un enfant par son nom ci-dessus.
              </p>
            )}

            {!chargement && semaine && enfants && enfants.length === 0 && (
              <p className="rounded-xl bg-white p-6 text-center text-sm text-gray-600 ring-1 ring-gray-200">
                Personne n’est inscrit cette semaine.
              </p>
            )}

            {!chargement && enfants && enfants.length > 0 && vue === 'enfants' && (
              <div className="space-y-4">
                {groupes.map(g => (
                  <section key={g.cle}>
                    <h2 className="mb-1.5 flex items-baseline justify-between gap-2 px-1">
                      <span className="truncate text-sm font-semibold text-gray-900">
                        {g.titre}
                        {g.sousTitre && <span className="font-normal text-gray-500"> · {g.sousTitre}</span>}
                      </span>
                      <span className="text-xs text-gray-500">{g.enfants.length}</span>
                    </h2>
                    <ul className="divide-y divide-gray-100 overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200">
                      {g.enfants.map(e => {
                        const lignes = lignesDe(details, e.kidId)
                        return (
                          <LigneEnfant
                            key={e.registrationId}
                            photo={photoDe(details, e.kidId)}
                            prenom={e.prenom}
                            nom={e.nom}
                            signes={lignes ? signes(lignes) : null}
                            sousTitre={sousTitre(details, e.kidId, e.dateNaissance)}
                            onOuvrir={() => setOuvert(e.kidId)}
                          />
                        )
                      })}
                    </ul>
                  </section>
                ))}
              </div>
            )}

            {!chargement && enfants && enfants.length > 0 && vue === 'a_savoir' && (
              <div className="space-y-4">
                {nbASavoir === 0 && (
                  <p className="rounded-xl bg-green-50 p-4 text-center text-sm text-green-800 ring-1 ring-green-200">
                    Rien à signaler cette semaine : ni allergie, ni médicament, ni besoin particulier, ni départ seul.
                  </p>
                )}
                {aSignalerParGroupe.filter(g => g.enfants.length > 0).map(({ groupe, enfants: liste }) => (
                  <section key={groupe.cle}>
                    <h2 className="mb-1.5 px-1 text-sm font-semibold text-gray-900">
                      {groupe.titre}
                      {groupe.sousTitre && <span className="font-normal text-gray-500"> · {groupe.sousTitre}</span>}
                    </h2>
                    <ul className="space-y-2">
                      {liste.map(({ enfant, lignes }) => (
                        <li key={enfant.registrationId} className="overflow-hidden rounded-2xl bg-white shadow-sm ring-1 ring-gray-200">
                          <button type="button" onClick={() => setOuvert(enfant.kidId)} className="block w-full p-3 text-left hover:bg-amber-50/40">
                            <div className="flex items-center gap-3">
                              <Avatar photo={photoDe(details, enfant.kidId)} prenom={enfant.prenom} nom={enfant.nom} taille="sm" />
                              <p className="min-w-0 flex-1 truncate text-base font-semibold text-gray-900">
                                {enfant.prenom} <span className="font-normal text-gray-700">{enfant.nom}</span>
                              </p>
                            </div>
                            <dl className="mt-2 space-y-1.5">
                              {lignes.map(l => <LigneASavoirVue key={l.cle} ligne={l} />)}
                            </dl>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                ))}
                <p className="text-center text-xs text-gray-400">
                  Les « tout va bien » et « RAS » sont écartés, comme dans les présences. Le tétanos, le médecin et les
                  téléphones sont dans la fiche.
                </p>
              </div>
            )}
          </section>
        )}
      </div>

      {doublons && (
        <DuplicateKidsPanel
          onClose={() => setDoublons(false)}
          onDeleted={() => setVersion(v => v + 1)}
        />
      )}
    </>
  )
}

function Erreur({ texte }: { texte: string }) {
  return (
    <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
      <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0" />
      <span>{texte}</span>
    </div>
  )
}
